import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiService, ApiEndpoints } from '../../services/ApiService';
import Alert from '@mui/material/Alert';

/**
 * Framework interface for dropdown
 */
interface Framework {
  id: number;
  name: string;
}

/**
 * Material Obligation Create/Edit Page
 * 
 * Form for creating new compliance obligations or editing existing ones
 */
interface MaterialObligationCreatePageProps {
  editId?: string;
}

export default function MaterialObligationCreatePage({ editId }: MaterialObligationCreatePageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const frameworkId = searchParams.get('frameworkId');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loadingFrameworks, setLoadingFrameworks] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  
  const isEditMode = !!editId;
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    source: '',
    priority: 'medium',
    framework_id: frameworkId || '',
    status: 'active'
  });

  // Fetch frameworks for dropdown
  useEffect(() => {
    const fetchFrameworks = async () => {
      try {
        setLoadingFrameworks(true);
        const response = await ApiService.get<any[]>(ApiEndpoints.FRAMEWORKS.LIST);
        setFrameworks(response);
      } catch (err) {
        console.error('Error fetching frameworks:', err);
        setError('Failed to load frameworks');
      } finally {
        setLoadingFrameworks(false);
      }
    };

    fetchFrameworks();
  }, []);
  
  // Load existing obligation data when editing
  useEffect(() => {
    if (isEditMode && editId) {
      loadObligationData();
    }
  }, [editId, isEditMode]);
  
  const loadObligationData = async () => {
    setLoadingData(true);
    setError('');
    
    try {
      const response = await ApiService.get(`/obligations/${editId}`);
      const obligation = response as any;
      
      setFormData({
        title: obligation.title || '',
        description: obligation.description || '',
        source: obligation.source || '',
        priority: obligation.priority || 'medium',
        framework_id: obligation.framework_id || obligation.frameworkId || '',
        status: obligation.status || 'active'
      });
    } catch (err: any) {
      console.error('Error loading obligation:', err);
      setError(err.message || 'Failed to load obligation');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let response;
      
      if (isEditMode) {
        // Update existing obligation
        response = await ApiService.put(`/obligations/${editId}`, formData);
      } else {
        // Create new obligation
        response = await ApiService.post(ApiEndpoints.OBLIGATIONS.CREATE, formData);
      }
      
      // Navigate to the obligation detail page
      navigate(`/obligations/${(response as any).id}`);
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} obligation:`, err);
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} obligation`);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/obligations');
  };

  if (loadingFrameworks || loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        {isEditMode ? 'Edit Obligation' : 'Create New Obligation'}
      </Typography>

      <Paper sx={{ p: 3, maxWidth: 800 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              id="obligation-title-input"
              name="title"
              label="Obligation Title"
              value={formData.title}
              onChange={handleChange}
              required
              fullWidth
              placeholder="e.g., Article 5 - Data Processing Principles"
            />

            <TextField
              id="obligation-description-input"
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleChange}
              required
              fullWidth
              multiline
              rows={4}
              placeholder="Describe the obligation requirements"
            />

            <FormControl fullWidth required>
              <InputLabel id="framework-select-label">Framework</InputLabel>
              <Select
                labelId="framework-select-label"
                id="framework-select"
                name="framework_id"
                value={formData.framework_id}
                onChange={handleChange}
                label="Framework"
              >
                {frameworks.map((framework) => (
                  <MenuItem key={framework.id} value={framework.id}>
                    {framework.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              id="obligation-source-input"
              name="source"
              label="Source Reference"
              value={formData.source}
              onChange={handleChange}
              fullWidth
              placeholder="e.g., GDPR Article 5"
            />

            <FormControl fullWidth>
              <InputLabel id="priority-label">Priority</InputLabel>
              <Select
                id="obligation-priority-select"
                labelId="priority-label"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                id="obligation-status-select"
                labelId="status-label"
                name="status"
                value={formData.status}
                onChange={handleChange}
                label="Status"
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                id="save-obligation-button"
                type="submit"
                variant="contained"
                disabled={loading || !formData.title || !formData.description || !formData.framework_id}
              >
                {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Obligation' : 'Create Obligation')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
